import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, startWith, takeUntil } from "rxjs/operators";

import {
	GlobalSearchService,
	PaletteGroup,
} from "src/app/_core/providers/global-search.service";
import { SearchEntry } from "src/app/_core/models/main/search-entry.model";

/**
 * Diálogo del Command Palette (Ctrl/Cmd+K). Búsqueda y navegación rápida a
 * Oportunidades, Contratos y Centros (datos reales con permisos del usuario).
 *
 * Teclado: ↑/↓ mueven el activo, Enter navega, Esc cierra (MatDialog).
 * Ratón: hover activa, click navega.
 */
@Component({
	standalone: false,
	selector: "app-command-palette-dialog",
	templateUrl: "./command-palette-dialog.component.html",
	styleUrls: ["./command-palette-dialog.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommandPaletteDialogComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	private readonly destroy$ = new Subject<void>();

	@ViewChild("input", { static: true }) input!: ElementRef<HTMLInputElement>;
	@ViewChild("list") list?: ElementRef<HTMLElement>;

	readonly query = new FormControl<string>("", { nonNullable: true });
	flat: SearchEntry[] = [];
	groups: PaletteGroup[] = [];
	activeIndex = 0;
	ready = false;
	countMessage = "";

	constructor(
		public dialogRef: MatDialogRef<CommandPaletteDialogComponent>,
		private readonly search: GlobalSearchService,
		private readonly router: Router,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.search.ready$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.ready = true;
			this.refresh(this.query.value);
		});
		this.query.valueChanges
			.pipe(startWith(""), debounceTime(60), takeUntil(this.destroy$))
			.subscribe((value) => this.refresh(value ?? ""));
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.input?.nativeElement?.focus();
			this.input?.nativeElement?.select();
		}, 0);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private refresh(query: string): void {
		const raw = this.search.search(query, 24);
		this.groups = this.search.groupByKind(raw);
		this.flat = this.groups.flatMap((g) => g.entries);
		this.activeIndex = this.flat.length ? 0 : -1;
		this.countMessage = this.flat.length
			? `${this.flat.length} resultado${this.flat.length === 1 ? "" : "s"}`
			: "";
		this.cdr.markForCheck();
	}

	isActive(entry: SearchEntry): boolean {
		return this.flat[this.activeIndex]?.id === entry.id;
	}

	indexOf(entry: SearchEntry): number {
		return this.flat.findIndex((e) => e.id === entry.id);
	}

	onPointerEnter(entry: SearchEntry): void {
		const idx = this.flat.findIndex((e) => e.id === entry.id);
		if (idx >= 0 && idx !== this.activeIndex) {
			this.activeIndex = idx;
			this.cdr.markForCheck();
		}
	}

	onClickEntry(entry: SearchEntry): void {
		this.navigate(entry);
	}

	private navigate(entry: SearchEntry): void {
		this.dialogRef.close();
		this.router.navigateByUrl(entry.url);
	}

	@HostListener("keydown", ["$event"])
	onKeydown(ev: KeyboardEvent): void {
		if (ev.key === "ArrowDown") {
			ev.preventDefault();
			if (!this.flat.length) {
				return;
			}
			this.activeIndex = (this.activeIndex + 1) % this.flat.length;
			this.scrollActiveIntoView();
			this.cdr.markForCheck();
		} else if (ev.key === "ArrowUp") {
			ev.preventDefault();
			if (!this.flat.length) {
				return;
			}
			this.activeIndex =
				(this.activeIndex - 1 + this.flat.length) % this.flat.length;
			this.scrollActiveIntoView();
			this.cdr.markForCheck();
		} else if (ev.key === "Enter") {
			const active = this.flat[this.activeIndex];
			if (active) {
				ev.preventDefault();
				this.navigate(active);
			}
		} else if (ev.key === "Home") {
			ev.preventDefault();
			this.activeIndex = this.flat.length ? 0 : -1;
			this.scrollActiveIntoView();
			this.cdr.markForCheck();
		} else if (ev.key === "End") {
			ev.preventDefault();
			this.activeIndex = this.flat.length - 1;
			this.scrollActiveIntoView();
			this.cdr.markForCheck();
		}
	}

	private scrollActiveIntoView(): void {
		const host = this.list?.nativeElement;
		if (!host) {
			return;
		}
		const el = host.querySelector<HTMLElement>(
			`[data-flat-index="${this.activeIndex}"]`,
		);
		el?.scrollIntoView({ block: "nearest" });
	}

	trackById = (_: number, entry: SearchEntry): string => entry.id;
}
